import { useMemo, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"

import { Button } from "@productlane/ui"
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandList,
} from "@productlane/ui/src/components/internal/command"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import type { FilterType } from "./filter-popover-items/types"
import type { RoadmapFilterNegationKey } from "./roadmap-filter-state"
import type { FilterChip } from "@/components/filters"
import { FilterControlChips } from "@/components/filters"
import { generateId } from "@/lib/id"
import { mutateOk, useZero } from "@/lib/zero"
import { CompanyFilterItems } from "./filter-popover-items/company-filter-items"
import {
	LabelFilterItems,
	useLabelOptions,
} from "./filter-popover-items/label-filter-items"
import { PriorityFilterItems } from "./filter-popover-items/priority-filter-items"
import { ProjectFilterItems } from "./filter-popover-items/project-filter-items"
import {
	ROADMAP_SOURCE_LABELS,
	SourceFilterItems,
} from "./filter-popover-items/source-filter-items"
import {
	ROADMAP_STATUS_LABELS,
	StatusFilterItems,
} from "./filter-popover-items/status-filter-items"
import { TeamFilterItems } from "./filter-popover-items/team-filter-items"
import { useRoadmapFilterState } from "./roadmap-filter-state"
import {
	sameRoadmapViewFilters,
	toRoadmapViewFilters,
	useActiveRoadmapView,
} from "./roadmap-views"
import { useRoadmapTeams } from "./use-roadmap-teams"

const CUSTOM_FILTER_LABELS: FilterType[] = ["Projects", "Teams", "Companies"]

export function RoadmapFilterControl() {
	const [projects] = useQuery(queries.projects())
	const teams = useRoadmapTeams()

	const {
		statuses,
		teamIds,
		projectIds,
		priorities,
		companyIds,
		sources,
		labelIds,
		negations,
		setNegation,
		clearStatuses,
		clearTeams,
		clearProjects,
		clearPriorities,
		clearCompanies,
		clearSources,
		clearLabels,
	} = useRoadmapFilterState()
	const labelOptions = useLabelOptions()
	const selectedLabelOptions = labelOptions.filter((option) =>
		option.ids.some((id) => labelIds.includes(id)),
	)
	const chipCompanyId = companyIds.length === 1 ? companyIds[0] : null
	const [chipCompany] = useQuery(queries.companyById(chipCompanyId ?? ""), {
		enabled: !!chipCompanyId,
	})

	const [query, setQuery] = useState("")

	const zero = useZero()
	const [views] = useQuery(queries.roadmapViews())
	const { activeViewId, setActiveViewId, setRenamingViewId } =
		useActiveRoadmapView()
	const activeView = views.find((view) => view.id === activeViewId)
	const currentFilters = toRoadmapViewFilters({
		negations,
		statuses,
		teamIds,
		projectIds,
		priorities,
		companyIds,
		sources,
		labelIds,
	})
	const hasFilters = Object.keys(currentFilters).length > 0
	const isViewChanged =
		!!activeView &&
		!sameRoadmapViewFilters(activeView.filters.roadmap ?? {}, currentFilters)

	const saveView = async () => {
		if (activeView) {
			await mutateOk(
				zero.mutate(
					mutators.customViews.update({
						id: activeView.id,
						filters: { roadmap: currentFilters },
					}),
				),
			)
			return
		}
		const id = generateId()
		await mutateOk(
			zero.mutate(
				mutators.customViews.create({
					id,
					name: "New view",
					slug: generateId().replace(/-/g, "").slice(0, 12),
					icon: null,
					filters: { roadmap: currentFilters },
					sortOrder: views.length,
					surface: "roadmap",
				}),
			),
		)
		setActiveViewId(id)
		setRenamingViewId(id)
	}

	const negationProps = (
		key: RoadmapFilterNegationKey,
		valueCount: number,
	) => ({
		valueCount,
		isNegated: negations[key] ?? false,
		onNegatedChange: (isNegated: boolean) => {
			setNegation(key, isNegated)
		},
	})

	const onFilterItemSelect = () => {
		setTimeout(() => setQuery(""), 100)
	}

	const renderEditor = (label: FilterType) => (
		<Command shouldFilter={!CUSTOM_FILTER_LABELS.includes(label)}>
			<CommandInput
				autoFocus
				placeholder="Filter..."
				value={query}
				onValueChange={setQuery}
			/>
			<CommandList className="max-h-[70dvh]">
				<CommandEmpty>Nothing found.</CommandEmpty>
				<CommandGroup>
					{label === "Statuses" && (
						<StatusFilterItems onSelect={onFilterItemSelect} />
					)}
					{label === "Teams" && (
						<TeamFilterItems query={query} onSelect={onFilterItemSelect} />
					)}
					{label === "Projects" && (
						<ProjectFilterItems query={query} onSelect={onFilterItemSelect} />
					)}
					{label === "Priorities" && (
						<PriorityFilterItems onSelect={onFilterItemSelect} />
					)}
					{label === "Companies" && (
						<CompanyFilterItems query={query} onSelect={onFilterItemSelect} />
					)}
					{label === "Sources" && (
						<SourceFilterItems onSelect={onFilterItemSelect} />
					)}
					{label === "Labels" && (
						<LabelFilterItems onSelect={onFilterItemSelect} />
					)}
				</CommandGroup>
			</CommandList>
		</Command>
	)

	const chips = useMemo<FilterChip[]>(() => {
		const result: FilterChip[] = []
		if (statuses.length > 0) {
			result.push({
				key: "Statuses",
				label: "Status",
				...negationProps("statuses", statuses.length),
				valueText:
					statuses.length === 1
						? (ROADMAP_STATUS_LABELS[statuses[0]!] ?? statuses[0]!)
						: `${statuses.length} statuses`,
				onClear: clearStatuses,
				popoverContent: renderEditor("Statuses"),
			})
		}
		// Team, priority and company carry no is / is not operator: a project
		// reaches all three through relations, and Zero's client cannot run the
		// not(exists()) that negating them needs.
		if (teamIds.length > 0) {
			result.push({
				key: "Teams",
				label: "Team",
				valueCount: teamIds.length,
				valueText:
					teamIds.length === 1
						? (teams.find((t) => t.ids.includes(teamIds[0]!))?.name ??
							teamIds[0]!)
						: `${teamIds.length} teams`,
				onClear: clearTeams,
				popoverContent: renderEditor("Teams"),
			})
		}
		if (projectIds.length > 0) {
			result.push({
				key: "Projects",
				label: "Project",
				...negationProps("projectIds", projectIds.length),
				valueText:
					projectIds.length === 1
						? (projects.find((p) => p.id === projectIds[0])?.name ??
							(projectIds[0] === null ? "No project" : ""))
						: projectIds.includes(null)
							? `No project and ${projectIds.length - 1} project${
									projectIds.length - 1 > 1 ? "s" : ""
								}`
							: `${projectIds.length} projects`,
				onClear: clearProjects,
				popoverContent: renderEditor("Projects"),
			})
		}
		if (priorities.length > 0) {
			result.push({
				key: "Priorities",
				label: "Priority",
				valueCount: priorities.length,
				valueText:
					priorities.length === 1
						? priorities[0]!
						: `${priorities.length} priorities`,
				onClear: clearPriorities,
				popoverContent: renderEditor("Priorities"),
			})
		}
		if (companyIds.length > 0) {
			result.push({
				key: "Companies",
				label: "Company",
				valueCount: companyIds.length,
				valueText:
					companyIds.length === 1
						? (chipCompany?.name ??
							(companyIds[0] === null ? "No company" : ""))
						: companyIds.includes(null)
							? `No company and ${companyIds.length - 1} compan${
									companyIds.length - 1 > 1 ? "ies" : "y"
								}`
							: `${companyIds.length} companies`,
				onClear: clearCompanies,
				popoverContent: renderEditor("Companies"),
			})
		}
		if (labelIds.length > 0) {
			result.push({
				key: "Labels",
				label: "Label",
				...negationProps("labelIds", selectedLabelOptions.length),
				valueText:
					selectedLabelOptions.length === 1
						? selectedLabelOptions[0]!.name
						: `${selectedLabelOptions.length} labels`,
				onClear: clearLabels,
				popoverContent: renderEditor("Labels"),
			})
		}
		if (sources.length === 1) {
			result.push({
				key: "Sources",
				label: "Source",
				valueCount: 1,
				valueText: ROADMAP_SOURCE_LABELS[sources[0]!],
				onClear: clearSources,
				popoverContent: renderEditor("Sources"),
			})
		}
		return result
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		sources,
		labelIds,
		labelOptions,
		statuses,
		teamIds,
		projectIds,
		priorities,
		companyIds,
		negations,
		projects,
		teams,
		chipCompany,
		query,
	])

	const showSave = activeView ? isViewChanged : hasFilters
	const tail = showSave ? (
		<Button className="ml-auto" type="button" onClick={() => void saveView()}>
			Save
		</Button>
	) : undefined

	return <FilterControlChips chips={chips} tailContent={tail} />
}
